import { type ReactElement } from 'react';
import { dailyNutritionSeries, recentMeals, nutritionSummary, type NutritionPoint } from '@/modules/contacts/clients/nutrition/lib/nutritionUtils';
import './nutrition-view.css';

function fmtDate(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return iso;
  const dt = new Date(y, m - 1, d);
  return Number.isNaN(dt.getTime()) ? iso : dt.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
}

function Sparkline({ series }: { series: NutritionPoint[] }) {
  const W = 280, H = 60, pad = 5;
  if (series.length < 2) return null;
  const vals = series.map((p) => p.value);
  const min = Math.min(...vals), max = Math.max(...vals);
  const span = max - min || 1;
  const n = series.length;
  const x = (i: number) => pad + (i * (W - pad * 2)) / Math.max(1, n - 1);
  const y = (v: number) => H - pad - ((v - min) / span) * (H - pad * 2);
  const pts = series.map((p, i) => `${x(i).toFixed(1)},${y(p.value).toFixed(1)}`).join(' ');
  return (
    <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" role="img" aria-label="Calories over time" style={{ width: '100%', height: 60 }}>
      <polyline points={`${pad},${H - pad} ${pts} ${W - pad},${H - pad}`} fill="rgba(21,165,227,.08)" stroke="none" />
      <polyline points={pts} fill="none" stroke="var(--color-brand)" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
      <circle cx={x(n - 1)} cy={y(series[n - 1].value)} r="3.2" fill="var(--color-brand)" />
    </svg>
  );
}

/** Minimal markdown: "- "/"•" lines → bullet list; blank lines split paragraphs. */
function AnalysisText({ text }: { text: string }) {
  const blocks: ReactElement[] = [];
  const lines = text.split('\n');
  let bullets: string[] = [];
  const flush = () => {
    if (bullets.length) {
      blocks.push(<ul key={`u${blocks.length}`} className="nv-analysis-list">{bullets.map((b, i) => <li key={i}>{b}</li>)}</ul>);
      bullets = [];
    }
  };
  for (const raw of lines) {
    const line = raw.trim();
    if (!line) { flush(); continue; }
    if (/^[-•*]\s+/.test(line)) { bullets.push(line.replace(/^[-•*]\s+/, '')); continue; }
    flush();
    blocks.push(<p key={`p${blocks.length}`}>{line}</p>);
  }
  flush();
  return <div className="nv-analysis-body">{blocks}</div>;
}

interface Props {
  stats: any[];
  analysis: { text: string; generatedAt: string } | null;
  onAnalyze?: () => void;
  analyzing?: boolean;
  /** Show the AI analysis sidebar. Off for the trainer view. Default true. */
  showAnalysis?: boolean;
}

export function NutritionConnectedView({ stats, analysis, onAnalyze, analyzing, showAnalysis = true }: Props) {
  const series = dailyNutritionSeries(stats);
  const summary = nutritionSummary(series);
  const days = recentMeals(stats, 7);

  if (series.calories.length === 0 && days.length === 0) {
    return <div className="nv-section"><p className="nv-muted">No nutrition data synced yet.</p></div>;
  }

  return (
    <div className={`nv-grid${showAnalysis ? '' : ' nv-grid--full'}`}>
      <div className="nv-main">
        <div className="nv-section">
          <h2>Summary</h2>
          <div className="nv-summary-grid">
            <div className="nv-stat-box"><div className="label">Today</div><div className="value">{summary.today !== null ? summary.today.toLocaleString() : '—'}</div><div className="sub">kcal</div></div>
            <div className="nv-stat-box"><div className="label">7-day avg</div><div className="value">{summary.avg7.toLocaleString()}</div><div className="sub">kcal</div></div>
            <div className="nv-stat-box"><div className="label">30-day avg</div><div className="value">{summary.avg30.toLocaleString()}</div><div className="sub">kcal</div></div>
            <div className="nv-stat-box"><div className="label">Protein / day</div><div className="value">{summary.proteinAvg30.toLocaleString()}</div><div className="sub">g avg</div></div>
          </div>
          {series.calories.length >= 2 && <Sparkline series={series.calories} />}
        </div>

        <div className="nv-section">
          <h2>Meal log</h2>
          {days.length === 0 ? (
            <p className="nv-muted">No meals synced yet.</p>
          ) : days.map((d) => (
            <div key={d.date} className="nv-day-group">
              <div className="nv-day-label">{fmtDate(d.date)} · <b>{d.totalCalories.toLocaleString()}</b> kcal</div>
              {d.meals.map((m) => (
                <div key={m.meal} className="nv-meal-block">
                  <div className="nv-meal-row">
                    <span className="nv-meal-name">{m.meal}</span>
                    <span className="nv-meal-cals">{m.calories.toLocaleString()} kcal · {Math.round(m.protein)}g protein</span>
                  </div>
                  {m.items.length > 0 && (
                    <ul className="nv-meal-foods">
                      {m.items.map((f, i) => (
                        <li key={i}><span className="nv-food-name">{f.name}</span><span className="nv-food-cals">{f.calories.toLocaleString()} kcal</span></li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>

      {showAnalysis && (
        <aside className="nv-side">
          <div className="nv-section nv-analysis-card">
            <div className="nv-section-head">
              <h2>AI analysis</h2>
              {onAnalyze && (
                <button type="button" className="nv-link-btn" onClick={onAnalyze} disabled={analyzing}>
                  {analyzing ? 'Analyzing…' : analysis ? 'Refresh' : 'Analyze'}
                </button>
              )}
            </div>
            {analysis && analysis.text ? (
              <>
                <AnalysisText text={analysis.text} />
                {analysis.generatedAt && (
                  <div className="nv-analysis-meta">Updated {fmtDate(String(analysis.generatedAt).slice(0, 10))}</div>
                )}
              </>
            ) : (
              <p className="nv-muted">{onAnalyze ? 'Get an AI read on your food intake — patterns, protein, and a few tips. Tap Analyze.' : 'No analysis yet.'}</p>
            )}
          </div>
        </aside>
      )}
    </div>
  );
}
